import { z } from 'zod';
import { cursorPageSchema, cursorQuerySchema, isoDateTimeSchema, localeSchema, uuidSchema, } from './common.js';
/**
 * E-mail delivery and editable templates (docs/partial/email-and-support-spec.md). The template
 * keys are listed once, here; every key has a default in the repository
 * (`backend-core/src/mail/templates`) and may have an override per language in the database.
 */
export const MAIL_TEMPLATE_KEYS = [
    'verify-email',
    'reset-password',
    'household-invite',
    'export-ready',
    'account-deleted',
    'email-changed',
    'password-changed',
    'support-reply',
    'support-received',
];
export const mailTemplateKeySchema = z.enum(MAIL_TEMPLATE_KEYS);
/** Which sender a template uses: account mails or the support address. */
export const mailPurposeSchema = z.enum(['ACCOUNT', 'SUPPORT']);
export const emailStatusSchema = z.enum([
    'QUEUED',
    'SENT',
    'DELIVERED',
    'DELAYED',
    'BOUNCED',
    'COMPLAINED',
    'FAILED',
    'SUPPRESSED',
    'RECEIVED',
]);
export const emailDirectionSchema = z.enum(['OUT', 'IN']);
// ---------------------------------------------------------------------------
// Status and settings
// ---------------------------------------------------------------------------
export const mailStatusSchema = z.object({
    transport: z.enum(['log', 'smtp', 'resend']),
    /** Secrets are never returned – only whether they are set. */
    apiKeySet: z.boolean(),
    webhookSecretSet: z.boolean(),
    fromAccount: z.string(),
    fromSupport: z.string(),
    supportInboundAddress: z.string().nullable(),
    appBaseUrl: z.string(),
    appBaseUrlIsHttps: z.boolean(),
    requireEmailVerification: z.boolean(),
    supportInboxEnabled: z.boolean(),
    webhookUrl: z.string(),
    lastWebhookEventAt: isoDateTimeSchema.nullable(),
    /** A delivery event reported click or open tracking – tokens would pass a third party. */
    trackingWarning: z.boolean(),
    counts: z.object({
        sent24h: z.number().int(),
        failed24h: z.number().int(),
        suppressed: z.number().int(),
        failedWebhookEvents: z.number().int(),
    }),
});
export const mailTestRequestSchema = z.object({ to: z.string().email().optional() });
export const mailTestResponseSchema = z.object({
    ok: z.boolean(),
    to: z.string(),
    messageId: uuidSchema.nullable(),
    error: z.string().nullable(),
});
const hexColorSchema = z.string().regex(/^#[0-9a-fA-F]{6}$/, 'Expected a colour like #d97b3a');
const httpsUrlSchema = z
    .string()
    .url()
    .max(500)
    .refine((v) => /^https:\/\//i.test(v), 'Expected an https URL');
export const mailSettingsSchema = z.object({
    appName: z.string().min(1).max(60),
    logoUrl: httpsUrlSchema.nullable(),
    primaryColor: hexColorSchema,
    /** Footer lines per language, e.g. the postal address. */
    footerLines: z.record(localeSchema, z.array(z.string().max(200)).max(6)),
    imprintUrl: httpsUrlSchema.nullable(),
    updatedAt: isoDateTimeSchema.nullable(),
});
export const mailSettingsUpdateSchema = mailSettingsSchema.omit({ updatedAt: true }).partial();
// ---------------------------------------------------------------------------
// Templates
// ---------------------------------------------------------------------------
export const mailTemplateVariableSchema = z.object({
    name: z.string(),
    /** `url` values may stand in `href`; `list` values are used with `{{#each}}`. */
    type: z.enum(['text', 'url', 'list']),
    required: z.boolean(),
    description: z.string(),
    sample: z.union([z.string(), z.array(z.string())]),
    /** Provided by the layout for every template (app name, support address, …). */
    global: z.boolean(),
});
export const mailTemplateSummarySchema = z.object({
    key: mailTemplateKeySchema,
    locale: localeSchema,
    purpose: mailPurposeSchema,
    subject: z.string(),
    customized: z.boolean(),
    version: z.number().int().nullable(),
    updatedAt: isoDateTimeSchema.nullable(),
    updatedByName: z.string().nullable(),
});
export const mailTemplateSchema = mailTemplateSummarySchema.extend({
    mjml: z.string(),
    /** Hand-written text part; null: derived from the HTML. */
    text: z.string().nullable(),
    variables: z.array(mailTemplateVariableSchema),
});
export const mailTemplateWriteSchema = z.object({
    subject: z.string().min(1).max(200),
    mjml: z.string().min(1).max(100_000),
    text: z.string().max(50_000).nullable().optional(),
});
export const mailTemplatePreviewRequestSchema = mailTemplateWriteSchema.extend({
    key: mailTemplateKeySchema,
    locale: localeSchema,
});
export const mailTemplateIssueSchema = z.object({
    /** Line in the template source; null when the problem has no place. */
    line: z.number().int().nullable(),
    message: z.string(),
});
export const mailTemplatePreviewSchema = z.object({
    subject: z.string(),
    html: z.string(),
    text: z.string(),
    errors: z.array(mailTemplateIssueSchema),
});
export const mailTemplateVersionSchema = z.object({
    version: z.number().int(),
    subject: z.string(),
    mjml: z.string(),
    text: z.string().nullable(),
    createdAt: isoDateTimeSchema,
    createdByName: z.string().nullable(),
});
// ---------------------------------------------------------------------------
// Delivery log, suppressions, webhook events
// ---------------------------------------------------------------------------
export const mailMessageSchema = z.object({
    id: uuidSchema,
    direction: emailDirectionSchema,
    templateKey: z.string().nullable(),
    fromAddress: z.string(),
    toAddress: z.string(),
    subject: z.string(),
    status: emailStatusSchema,
    providerId: z.string().nullable(),
    error: z.string().nullable(),
    incident: z.string().nullable(),
    userId: uuidSchema.nullable(),
    conversationId: uuidSchema.nullable(),
    createdAt: isoDateTimeSchema,
    sentAt: isoDateTimeSchema.nullable(),
    deliveredAt: isoDateTimeSchema.nullable(),
});
export const mailMessagePageSchema = cursorPageSchema(mailMessageSchema);
export const mailMessageQuerySchema = cursorQuerySchema.extend({
    status: emailStatusSchema.optional(),
    template: z.string().max(60).optional(),
    to: z.string().max(200).optional(),
});
export const mailSuppressionSchema = z.object({
    id: uuidSchema,
    address: z.string(),
    reason: z.enum(['BOUNCE', 'COMPLAINT', 'BLOCKED_SENDER']),
    providerDetail: z.string().nullable(),
    createdAt: isoDateTimeSchema,
});
export const mailSuppressionPageSchema = cursorPageSchema(mailSuppressionSchema);
export const webhookEventSchema = z.object({
    id: uuidSchema,
    provider: z.string(),
    type: z.string(),
    status: z.enum(['RECEIVED', 'PROCESSED', 'FAILED', 'IGNORED']),
    error: z.string().nullable(),
    attempts: z.number().int(),
    createdAt: isoDateTimeSchema,
    processedAt: isoDateTimeSchema.nullable(),
});
export const webhookEventPageSchema = cursorPageSchema(webhookEventSchema);
export const webhookEventQuerySchema = cursorQuerySchema.extend({
    status: z.enum(['RECEIVED', 'PROCESSED', 'FAILED', 'IGNORED']).optional(),
});
//# sourceMappingURL=mail.js.map